import { isAcTerminalId } from "./ac";
import { clearAiEstimates } from "./ai/component-draft";
import { dcBreakerSpec } from "./dc-breaker";
import type { ComponentDefinition, ComponentNode } from "./types";

/** A name or a terminal's ampacity does not establish a protection device. */
export function canConfigureDcBreaker(def: ComponentDefinition): boolean {
  return def.id === "custom" && !def.requestedModel &&
    (def.category === "distribution" || def.category === "load") &&
    (def.renderVariant == null || def.renderVariant === "card") &&
    !def.isFuseBlock && !def.fuseSlotType && !def.dcBreaker && !def.acVoltage &&
    !def.internalDcConnections && !def.oneWayDcConnections && !def.currentShunt &&
    !def.simulateStates && !def.settings && !def.bms &&
    def.inputVoltage == null && def.outputVoltage == null && def.ampOutput == null &&
    def.terminals.length === 2 && new Set(def.terminals.map(t => t.id)).size === 2 &&
    def.terminals.every(t => t.id.length > 0 && t.polarity === "positive" && !isAcTerminalId(t.id));
}

export type CustomDcBreakerSettings = {
  inputTerminalId: string;
  ratingA: number;
  dcMaxVoltage?: number;
};

/** Explicitly declare a single-pole breaker while retaining the drawing. */
export function configureDcBreaker(node: ComponentNode, settings: CustomDcBreakerSettings): ComponentNode | null {
  const def = node.customDefinition;
  if (node.definitionId !== "custom" || !def ||
    !(canConfigureDcBreaker(def) || dcBreakerSpec(def)) ||
    !Number.isFinite(settings.ratingA) || settings.ratingA <= 0 ||
    (settings.dcMaxVoltage != null && (!Number.isFinite(settings.dcMaxVoltage) || settings.dcMaxVoltage <= 0))) return null;
  const input = def.terminals.find(t => t.id === settings.inputTerminalId);
  const output = def.terminals.find(t => t.id !== settings.inputTerminalId);
  if (!input || !output) return null;
  const keepInterruptRatings = def.dcBreaker?.ratingA === settings.ratingA &&
    def.dcBreaker?.dcMaxVoltage === settings.dcMaxVoltage && !!def.dcBreaker?.interruptRatings;
  const next: ComponentDefinition = {
    ...def,
    category: "distribution",
    dcBreaker: { ...settings, outputTerminalId: output.id,
      ...(keepInterruptRatings ? { interruptRatings: def.dcBreaker!.interruptRatings } : {}) },
    terminals: def.terminals.map(t => ({ ...t, direction: t.id === input.id ? "input" : "output" })),
    simulateStates: { default: "on", options: [
      { id: "on", label: "On", intraLinks: [[input.id, output.id]] },
      { id: "off", label: "Off", ampOverride: 0, intraLinks: [] },
      { id: "tripped", label: "Tripped", ampOverride: 0, intraLinks: [] },
    ] },
  };
  // These were load declarations, never breaker current or voltage ratings.
  const removed = ["ampDraw", "ampSurge", "watts", "voltage", "loadCurrentModel", "alwaysOn", "dutyHoursPerDay"] as const;
  for (const key of removed) delete next[key];
  const { dutyHoursPerDayOverride: _duty, alwaysOnOverride: _always, ...rest } = node;
  // Editing the terminal or confirming current/max-voltage does not confirm
  // any retained interrupt-capacity evidence nested in the same field.
  return { ...rest, customDefinition: clearAiEstimates(next, ["category", ...(keepInterruptRatings ? [] : ["dcBreaker"]), "terminals", "simulateStates", ...removed]) };
}
